// Ficha de Mercado Libre de un producto: sus publicaciones, calidad, visitas y los cambios de
// precio y descripción. Todo lo hace atlas-backend con el token de la conexión.

import { atlasBackendFetch, atlasBackendGet, pollJobUntilSettled } from "./atlasBackend";
import { codigoDeError, ErrorDeMercadoLibre } from "./mercadoLibreConexionLogica";
import type { CalidadDeMl, PublicacionDelProducto, ResultadoCampo } from "./mercadoLibreDatosLogica";
import { exigirPermisoDeTienda } from "./permisosDelEquipo";

async function leerJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

/** Error con el código y el mensaje del backend (los 409 de "¿seguro?" traen su texto). */
async function pedir<T>(fn: () => Promise<Response>): Promise<T> {
  let r: Response;
  try {
    r = await fn();
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  const cuerpo = await leerJson(r);
  if (!r.ok) {
    const e = new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
    const msg = (cuerpo as { message?: unknown } | null)?.message;
    // Los avisos del backend ya vienen en castellano ("¿seguro?", "precio automático"…); los de
    // validación del esquema no, y ésos quedan con el mensaje genérico.
    if (typeof msg === "string" && [400, 403, 404, 409].includes(r.status) && e.code !== "validacion") e.message = msg;
    throw e;
  }
  return cuerpo as T;
}

export async function publicacionesDelProducto(productoId: string): Promise<PublicacionDelProducto[]> {
  const r = await pedir<{ publicaciones?: PublicacionDelProducto[] }>(() =>
    atlasBackendGet(`/v1/mercadolibre/producto?producto_id=${encodeURIComponent(productoId)}`),
  );
  return r.publicaciones ?? [];
}

export async function calidadDeMl(itemId: string, fresco = false): Promise<CalidadDeMl> {
  const r = await pedir<{ calidad: CalidadDeMl }>(() =>
    atlasBackendGet(`/v1/mercadolibre/calidad?item_id=${encodeURIComponent(itemId)}&fresco=${fresco ? "si" : "no"}`),
  );
  return r.calidad;
}

export async function visitasDeMl(itemId: string): Promise<number> {
  const r = await pedir<{ visitas: { total: number } }>(() =>
    atlasBackendGet(`/v1/mercadolibre/visitas?item_id=${encodeURIComponent(itemId)}&dias=30`),
  );
  return r.visitas.total;
}

export async function descripcionDeMl(itemId: string): Promise<string> {
  const r = await pedir<{ descripcion: string }>(() =>
    atlasBackendGet(`/v1/mercadolibre/descripcion?item_id=${encodeURIComponent(itemId)}`),
  );
  return r.descripcion ?? "";
}

/** Encola el cambio. Un 409 con código de `PIDEN_CONFIRMAR` es un "¿seguro?": se reintenta con `confirmado`. */
export async function cambiarDatosEnMl(p: {
  itemId: string;
  precio?: number | null;
  descripcion?: string | null;
  /** Stock y activa/pausada (ADR-2026-10-30-un-cambio-en-varias-tiendas). */
  stock?: { general?: number | null; porValores?: Array<{ valores: string[]; stock: number }> } | null;
  estado?: "active" | "paused" | null;
  confirmado?: boolean;
}): Promise<string> {
  await exigirPermisoDeTienda();
  const r = await pedir<{ execution_id?: string }>(() =>
    atlasBackendFetch("/v1/mercadolibre/cambiar-datos", {
      item_id: p.itemId,
      ...(typeof p.precio === "number" ? { precio: p.precio } : {}),
      ...(typeof p.descripcion === "string" ? { descripcion: p.descripcion } : {}),
      ...(p.stock ? { stock: p.stock } : {}),
      ...(p.estado ? { estado: p.estado } : {}),
      confirmado: p.confirmado === true,
    }),
  );
  if (!r.execution_id) throw new ErrorDeMercadoLibre("error");
  return r.execution_id;
}

export async function esperarCambioEnMl(
  executionId: string,
  signal?: AbortSignal,
): Promise<{ precio?: ResultadoCampo; descripcion?: ResultadoCampo; stock?: ResultadoCampo; estado?: ResultadoCampo } | null> {
  const r = await pollJobUntilSettled(executionId, { timeoutMs: 2 * 60_000, intervalMs: 2000, signal });
  if (!r) return null;
  if (r.status === "failed") {
    const e = new ErrorDeMercadoLibre(r.errorType === "auth" ? "reconectar" : "error");
    if (r.userMessage) e.message = r.userMessage;
    throw e;
  }
  return (r.output ?? null) as {
    precio?: ResultadoCampo;
    descripcion?: ResultadoCampo;
    stock?: ResultadoCampo;
    estado?: ResultadoCampo;
  } | null;
}
